import React, { useEffect, useCallback, useState } from "react";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { FaBars } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import logo from "../../../Assets/logo/wbclogo.svg";
import AppointmentModal from "../Common/AppointmentModal";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Courses", to: "/courses" },
  { label: "Articles", to: "/articles" },
  { label: "Career", to: "/careers" },
  { label: "Contact Us", to: "/contact" },
];

const baseLink =
  "text-base font-normal tracking-wide transition-colors duration-200 cursor-pointer";
const activeLink = "text-[#064585]";
const hoverLink = "hover:text-[#064585]";

const BottomNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  // ESC to close drawer
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // Auto-open appointment modal at 30s, 90s, 150s
  useEffect(() => {
    const delays = [30000, 90000, 150000];
    const timers = delays.map((ms) => setTimeout(() => setModalOpen(true), ms));
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <>
      <nav className="backdrop-blur-md bg-gradient-to-r from-[#eef3ff]/80 via-[#f7f5ff]/80 to-white/80 shadow-lg  border-b border-white/40">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="shrink-0 flex items-center gap-2"
              aria-label="Go to home"
            >
              <img src={logo} alt="baisaly-logo" className="h-12 w-auto" />
            </Link>

            {/* Links (md+) */}
            <ul className="hidden lg:flex items-center gap-8">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    onClick={() => handleNavigate(item.to)}
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `${baseLink} ${isActive ? activeLink : hoverLink}`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* CTA (md+) */}
            <div className="hidden lg:block">
              <button
                onClick={openModal}
                className="tracking-wide bg-[#064585] text-white py-2.5 px-6 rounded-xl font-medium shadow-sm hover:shadow-md hover:shadow-blue-200 transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#064585]"
              >
                Talk to Us
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={openDrawer}
              className="lg:hidden inline-flex items-center justify-center p-2 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#064585]"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer"
            >
              <FaBars size={22} />
            </button>
          </div>
        </div>
      </nav>

      {/* Appointment Modal */}
      {modalOpen && <AppointmentModal onClose={closeModal} />}

      {/* Mobile Drawer */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${
          drawerOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        aria-hidden={!drawerOpen}
      >
        {/* Backdrop */}
        <div
          onClick={closeDrawer}
          className={`absolute inset-0 bg-black transition-opacity ${
            drawerOpen ? "opacity-50" : "opacity-0"
          }`}
        />

        {/* Panel */}
        <aside
          id="mobile-drawer"
          role="dialog"
          aria-modal="true"
          className={`absolute left-0 top-0 h-full w-72 bg-white shadow-xl transform transition-transform duration-300 ${
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="p-4 h-full flex flex-col">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center gap-2"
                onClick={closeDrawer}
              >
                <img src={logo} alt="baisaly-logo" className="h-10 w-auto" />
              </Link>

              <button
                onClick={closeDrawer}
                aria-label="Close menu"
                className="p-2 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#064585]"
              >
                <IoClose size={24} className="text-[#064585]" />
              </button>
            </div>

            <ul className="mt-8 space-y-4 text-lg">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `${baseLink} block ${isActive ? activeLink : hoverLink}`
                    }
                    onClick={closeDrawer}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-6">
              <button
                onClick={() => {
                  closeDrawer();
                  openModal();
                }}
                className="w-full tracking-wide bg-[#064585] text-white font-semibold py-3 px-4 rounded hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#064585]"
              >
                Talk to Us
              </button>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default BottomNavbar;
